import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Copy, Loader2, Trash2, Clock, Eye, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = [
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'retail', label: 'Retail & Consumer' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'technology', label: 'Technology' },
  { value: 'public_sector', label: 'Public Sector' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'manufacturing', label: 'Manufacturing' },
];

const DRAWER_SCREENS = [
  "Signal Room", "Blueprint Cockpit", "The Stage",
  "Audition Room", "Org Intelligence", "Showcase",
];

function formatLastVisited(isoString) {
  if (!isoString) return { text: "Never", className: "text-gray-500" };
  const diff = Date.now() - new Date(isoString).getTime();
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diff / 3600000);
    return { text: hours <= 1 ? "Just now" : `${hours}h ago`, className: "text-teal-400" };
  }
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    const days = Math.floor(diff / 86400000);
    return { text: `${days}d ago`, className: "text-white" };
  }
  return {
    text: new Date(isoString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    className: "text-gray-400",
  };
}

const AdminDemos = () => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdDemo, setCreatedDemo] = useState(null);
  const [form, setForm] = useState({
    prospect_name: '', org_name: '', sector: 'financial_services',
    headcount_band: '1000-2499', currency: 'GBP', expires_days: 14,
  });

  // Filter/sort
  const [filterSector, setFilterSector] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');

  // Activity drawer
  const [drawerDemo, setDrawerDemo] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const token = localStorage.getItem('token');

  const fetchDemos = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/admin/demos`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (resp.ok) setDemos(await resp.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchDemos(); }, [fetchDemos]);

  const openDrawer = (demo) => {
    setDrawerDemo(demo);
    setActivityData(null);
    setActivityLoading(true);
    fetch(`${API}/admin/demos/${demo.slug}/activity`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setActivityData(data); setActivityLoading(false); })
      .catch(() => setActivityLoading(false));
  };

  const closeDrawer = () => { setDrawerDemo(null); setActivityData(null); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.prospect_name) { toast.error('Prospect name is required'); return; }
    setCreating(true);
    try {
      const resp = await fetch(`${API}/admin/demos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...form, org_name: form.org_name || form.prospect_name }),
      });
      if (!resp.ok) throw new Error('Failed');
      const data = await resp.json();
      setCreatedDemo(data);
      toast.success('Demo created');
      fetchDemos();
    } catch { toast.error('Failed to create demo'); }
    finally { setCreating(false); }
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/demo/${slug}`);
    toast.success('Link copied');
  };

  const revoke = async (id) => {
    await fetch(`${API}/admin/demos/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    toast.success('Demo revoked');
    fetchDemos();
  };

  const extend = async (id) => {
    await fetch(`${API}/admin/demos/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ extend_days: 7 }),
    });
    toast.success('Extended by 7 days');
    fetchDemos();
  };

  const getStatus = (demo) => {
    if (!demo.is_active) return { label: 'Revoked', color: 'text-muted-foreground' };
    const exp = new Date(demo.expires_at);
    if (exp < new Date()) return { label: 'Expired', color: 'text-red-400' };
    return { label: 'Active', color: 'text-green-400' };
  };

  // Client-side filter + sort
  const filtered = demos.filter(d => {
    if (filterSector !== 'all' && d.sector !== filterSector) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = (d.prospect_name || '').toLowerCase().includes(q) || (d.org_name || '').toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (sortBy === 'most_viewed') return (b.view_count || 0) - (a.view_count || 0);
    if (sortBy === 'recently_visited') {
      const av = a.last_visited ? new Date(a.last_visited).getTime() : 0;
      const bv = b.last_visited ? new Date(b.last_visited).getTime() : 0;
      return bv - av;
    }
    if (sortBy === 'alphabetical') return (a.prospect_name || '').localeCompare(b.prospect_name || '');
    return 0;
  });

  return (
    <AdminLayout title="Demo Management" breadcrumbs={[{ label: 'Demos' }]}>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground">Create and manage prospect demo access</p>
          <Button onClick={() => { setShowForm(!showForm); setCreatedDemo(null); }}>
            <Plus className="h-4 w-4 mr-2" />{showForm ? 'Cancel' : 'Create Demo'}
          </Button>
        </div>

        {/* Creation form */}
        {showForm && !createdDemo && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <form onSubmit={handleCreate} className="grid grid-cols-2 gap-4">
                <div><Label>Prospect Name *</Label><Input value={form.prospect_name} onChange={(e) => setForm({ ...form, prospect_name: e.target.value })} placeholder="John Smith" /></div>
                <div><Label>Organisation</Label><Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder="Acme Corp" /></div>
                <div><Label>Sector</Label>
                  <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{SECTORS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Headcount Band</Label>
                  <Select value={form.headcount_band} onValueChange={(v) => setForm({ ...form, headcount_band: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="500-999">500-999</SelectItem><SelectItem value="1000-2499">1,000-2,499</SelectItem><SelectItem value="2500-4999">2,500-4,999</SelectItem><SelectItem value="5000+">5,000+</SelectItem></SelectContent></Select>
                </div>
                <div><Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="GBP">GBP</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="AED">AED</SelectItem><SelectItem value="INR">INR</SelectItem><SelectItem value="MUR">MUR</SelectItem></SelectContent></Select>
                </div>
                <div><Label>Expires In</Label>
                  <Select value={String(form.expires_days)} onValueChange={(v) => setForm({ ...form, expires_days: parseInt(v) })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="7">7 days</SelectItem><SelectItem value="14">14 days</SelectItem><SelectItem value="30">30 days</SelectItem><SelectItem value="60">60 days</SelectItem></SelectContent></Select>
                </div>
                <div className="col-span-2 flex justify-end gap-2 mt-2">
                  <Button variant="outline" type="button" onClick={() => setShowForm(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating}>{creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : 'Generate Demo Link'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {createdDemo && (
          <Card className="mb-6 border-primary/30">
            <CardContent className="p-6 text-center">
              <p className="text-green-400 font-medium mb-3">Demo created for {form.prospect_name}</p>
              <div className="p-4 bg-background rounded-lg border mb-4"><p className="font-mono text-sm break-all">{window.location.origin}/demo/{createdDemo.slug}</p></div>
              <div className="flex justify-center gap-2">
                <Button onClick={() => copyLink(createdDemo.slug)}><Copy className="h-4 w-4 mr-2" />Copy Link</Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setCreatedDemo(null); }}>Done</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter/Sort bar */}
        <div className="flex gap-3 mb-4 flex-wrap items-center">
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded px-3 py-1.5 text-sm outline-none">
            <option value="all">All Industries</option>
            {SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="bg-white/5 border border-white/10 text-white rounded px-3 py-1.5 text-sm outline-none">
            <option value="newest">Sort: Newest</option>
            <option value="most_viewed">Sort: Most Viewed</option>
            <option value="recently_visited">Sort: Recently Visited</option>
            <option value="alphabetical">Sort: Alphabetical</option>
          </select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search..." className="w-full bg-white/5 border border-white/10 text-white rounded pl-8 pr-3 py-1.5 text-sm outline-none placeholder:text-muted-foreground" />
          </div>
        </div>

        {/* Demo list */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground">{demos.length === 0 ? 'No demos created yet' : 'No demos match your filters'}</CardContent></Card>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-card/50">
                  {['Prospect', 'Org', 'Sector', 'Created', 'Views', 'Last Visited', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const status = getStatus(d);
                  return (
                    <tr key={d.id || d.slug} className="border-b hover:bg-white/5 transition-colors cursor-pointer" onClick={() => openDrawer(d)}>
                      <td className="px-4 py-3 font-medium">{d.prospect_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{d.org_name}</td>
                      <td className="px-4 py-3"><Badge variant="outline">{SECTORS.find((s) => s.value === d.sector)?.label || d.sector_display || d.sector}</Badge></td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3"><span className="flex items-center gap-1 text-muted-foreground"><Eye className="h-3 w-3" />{d.view_count || 0}</span></td>
                      {(() => { const lv = formatLastVisited(d.last_visited); return <td className={`px-4 py-3 text-xs ${lv.className}`}>{lv.text}</td>; })()}
                      <td className="px-4 py-3"><span className={`text-xs font-medium ${status.color}`}>{status.label}</span></td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => copyLink(d.slug)}><Copy className="h-3 w-3" /></Button>
                          {status.label === 'Active' && <Button variant="ghost" size="sm" onClick={() => revoke(d.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>}
                          <Button variant="ghost" size="sm" onClick={() => extend(d.id)}><Clock className="h-3 w-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Activity drawer */}
      {drawerDemo && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={closeDrawer} />
          <div className="fixed top-0 right-0 h-full w-[480px] max-w-full bg-[#0f0f1a] border-l border-white/10 z-50 overflow-y-auto flex flex-col" style={{ animation: 'slideInRight 0.25s ease-out' }}>
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b border-white/10 sticky top-0 bg-[#0f0f1a] z-10">
              <div>
                <h2 className="text-white font-semibold text-lg leading-tight">{drawerDemo.prospect_name}</h2>
                <p className="text-gray-400 text-sm mt-0.5">{drawerDemo.org_name}</p>
              </div>
              <button onClick={closeDrawer} className="text-gray-400 hover:text-white ml-4 mt-0.5 text-xl leading-none">&#10005;</button>
            </div>

            {/* Content */}
            <div className="p-6 flex-1">
              {activityLoading && (
                <div className="flex items-center justify-center h-40">
                  <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              {!activityLoading && activityData && activityData.total_visits === 0 && (
                <div className="flex flex-col items-center justify-center h-40 text-gray-500 gap-3">
                  <svg className="w-10 h-10 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <p className="text-sm">No visits recorded yet</p>
                </div>
              )}

              {!activityLoading && activityData && activityData.total_visits > 0 && (
                <>
                  {/* Visit summary strip */}
                  <div className="flex gap-6 mb-6 p-4 bg-white/5 rounded-lg">
                    <div>
                      <p className="text-gray-400 text-xs uppercase tracking-wide">Total Visits</p>
                      <p className="text-white text-2xl font-bold mt-0.5">{activityData.total_visits}</p>
                    </div>
                    <div className="border-l border-white/10 pl-6">
                      <p className="text-gray-400 text-xs uppercase tracking-wide">Last Visited</p>
                      <p className="text-teal-400 text-sm mt-0.5 font-medium">{formatLastVisited(activityData.last_visited).text}</p>
                    </div>
                  </div>

                  {/* Screen engagement bars */}
                  <h3 className="text-white text-sm font-semibold mb-3 uppercase tracking-wide">Screen Engagement</h3>
                  <div className="space-y-3 mb-6">
                    {(() => {
                      const ss = activityData.screen_summary || {};
                      const maxVisits = Math.max(1, ...DRAWER_SCREENS.map(s => ss[s]?.visits || 0));
                      return DRAWER_SCREENS.map(screen => {
                        const s = ss[screen] || { visits: 0, avg_duration_seconds: 0 };
                        const pct = (s.visits / maxVisits) * 100;
                        const avgLabel = s.avg_duration_seconds > 0 ? `${Math.floor(s.avg_duration_seconds / 60)}m ${Math.round(s.avg_duration_seconds % 60)}s avg` : null;
                        return (
                          <div key={screen}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-300">{screen}</span>
                              <span className="text-gray-500">{s.visits > 0 ? `${s.visits} visit${s.visits > 1 ? 's' : ''}${avgLabel ? ' \u00b7 ' + avgLabel : ''}` : '\u2014'}</span>
                            </div>
                            <div className="h-1.5 bg-white/10 rounded-full">
                              <div className="h-1.5 bg-teal-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {/* Visit timeline */}
                  <h3 className="text-white text-sm font-semibold mb-3 uppercase tracking-wide">Visit Timeline</h3>
                  <div className="space-y-4">
                    {(activityData.visits || []).map((visit, vi) => (
                      <div key={visit.session_id || vi} className="border-l-2 border-teal-400/40 pl-4">
                        <p className="text-gray-300 text-xs font-medium mb-2">
                          Visit {vi + 1} · {visit.started_at ? new Date(visit.started_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                          {visit.started_at ? ` \u00b7 ${new Date(visit.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}
                        </p>
                        <div className="space-y-1.5">
                          {(visit.screens || []).map((sc, si) => (
                            <div key={si}>
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-400">{'\u251C'} {sc.screen}</span>
                                <span className="text-gray-600">{Math.floor((sc.duration_seconds || 0) / 60)}m {Math.round((sc.duration_seconds || 0) % 60)}s</span>
                              </div>
                              {(sc.actions || []).map((a, ai) => (
                                <p key={ai} className="text-gray-600 text-xs pl-3 mt-0.5">{'\u2192'} {a}</p>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <style>{`@keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDemos;
